import React, { useState, useEffect } from 'react';
import { useBackClose } from './backButton';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useLanguage, dateLocale, serviceTypeName } from './i18n';
import './ServicePanel.css';
import { translateText, detectLanguage } from './translationUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SP_T = {
  kk: {
    panelTitle: 'Қызмет панелі',
    activeEvents: 'Белсенді оқиғалар',
    loadingEvents: 'Оқиғалар жүктелуде...',
    noActiveEvents: 'Белсенді оқиғалар жоқ',
    loadEventsError: 'Оқиғаларды жүктеу қатесі',
    loadDetailsError: 'Оқиға мәліметтерін жүктеу қатесі',
    updateError: 'Оқиғаны жаңарту кезінде қате',
    cancelError: 'Оқиғаны болдырмау кезінде қате',
    statusUpdated: 'Оқиға мәртебесі сәтті жаңартылды',
    eventCancelled: 'Оқиға сәтті болдырылмады',
    cancelReasonPrompt: 'Оқиғаны болдырмау себебін көрсетіңіз (жалған, маңызды емес, құзыретте емес және т.б.):',
    cancelReasonRequired: 'Болдырмау себебін көрсету қажет',
    cancelConfirm: (reason) => `Оқиғаны болдырмағыңыз келе ме?\nСебебі: ${reason}`,
    priority: 'Маңыздылық',
    eventDetails: 'Оқиға мәліметтері',
    description: 'Сипаттама:',
    priorityLabel: 'Маңыздылық:',
    creator: 'Жасаған:',
    reporterName: 'Өтініш берушінің аты-жөні:',
    reporterPhone: 'Өтініш берушінің телефоны:',
    address: 'Мекенжай:',
    notSpecified: 'Көрсетілмеген',
    comments: 'Пікірлер:',
    userFallback: 'Пайдаланушы',
    updateStatus: 'Мәртебені жаңарту',
    statusLabel: 'Мәртебе:',
    priorityOptions: ['1 - Төмен', '2 - Қалыпты', '3 - Орташа', '4 - Жоғары', '5 - Дағдарысты'],
    serviceComment: 'Қызмет пікірі (барлық пайдаланушылар үшін):',
    commentPlaceholder: 'Оқиғаның ағымдағы жағдайы туралы қызмет жауабы...',
    updating: 'Жаңартылуда...',
    updateBtn: 'Мәртебені жаңарту',
    cancelBtn: 'Оқиғаны болдырмау',
    categories: {
      emergency: '🚨 ТЖ',
      repairs: '🛠 Жөндеу',
      utilities: '💧 ТКШ',
      transport: '🚌 Көлік',
      infra: '🏢 Инфрақұрылым'
    }
  },
  ru: {
    panelTitle: 'Панель службы',
    activeEvents: 'Активные события',
    loadingEvents: 'Загрузка событий...',
    noActiveEvents: 'Нет активных событий',
    loadEventsError: 'Ошибка загрузки событий',
    loadDetailsError: 'Ошибка загрузки деталей события',
    updateError: 'Ошибка при обновлении события',
    cancelError: 'Ошибка при отмене события',
    statusUpdated: 'Статус события успешно обновлен',
    eventCancelled: 'Событие успешно отменено',
    cancelReasonPrompt: 'Укажите причину отмены события (фейковое, малозначительное, не в компетенции и т.д.):',
    cancelReasonRequired: 'Необходимо указать причину отмены',
    cancelConfirm: (reason) => `Вы уверены, что хотите отменить событие?\nПричина: ${reason}`,
    priority: 'Приоритет',
    eventDetails: 'Детали события',
    description: 'Описание:',
    priorityLabel: 'Приоритет:',
    creator: 'Создатель:',
    reporterName: 'ФИО заявителя:',
    reporterPhone: 'Телефон заявителя:',
    address: 'Адрес:',
    notSpecified: 'Не указан',
    comments: 'Комментарии:',
    userFallback: 'Пользователь',
    updateStatus: 'Обновить статус',
    statusLabel: 'Статус:',
    priorityOptions: ['1 - Низкий', '2 - Обычный', '3 - Средний', '4 - Высокий', '5 - Критический'],
    serviceComment: 'Комментарий службы (для всех пользователей):',
    commentPlaceholder: 'Ответ службы о текущем состоянии события...',
    updating: 'Обновление...',
    updateBtn: 'Обновить статус',
    cancelBtn: 'Отменить событие',
    categories: {
      emergency: '🚨 ЧС',
      repairs: '🛠 Ремонты',
      utilities: '💧 ЖКХ',
      transport: '🚌 Транспорт',
      infra: '🏢 Инфраструктура'
    }
  }
};

const ServicePanel = ({ onClose, eventId = null }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = SP_T[language];
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  // Кнопка «Назад» закрывает карточку события
  useBackClose(!!selectedEvent, () => setSelectedEvent(null));
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    estimated_completion: '',
    priority: 1
  });
  const [comment, setComment] = useState({
    comment_ru: '',
    comment_kk: ''
  });

  const statusOptions = [
    { value: 'new', label_ru: 'Новое', label_kk: 'Жаңа' },
    { value: 'assigned', label_ru: 'Назначено', label_kk: 'Тағайындалған' },
    { value: 'in_progress', label_ru: 'В работе', label_kk: 'Орындалуда' },
    { value: 'completed', label_ru: 'Выполнено', label_kk: 'Аяқталған' },
    { value: 'rejected', label_ru: 'Отклонено', label_kk: 'Қабылданбаған' }
  ];

  useEffect(() => {
    loadEvents();
    if (eventId) {
      loadEventDetails(eventId);
    }
  }, [eventId]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      // Get events assigned to this specific service user
      const response = await axios.get(`${API}/events?assigned_to_me=true`);
      console.log('Service Panel: Events assigned to me loaded:', response.data.length);
      
      // Filter only active events that service can work with
      const serviceEvents = response.data.filter(event => {
        const canWork = event.status !== 'completed' && 
                       event.status !== 'closed' && 
                       event.status !== 'final_completed';
        console.log(`Event ${event.id}: status=${event.status}, canWork=${canWork}, assigned to this service`);
        return canWork;
      });
      
      console.log('Service Panel: Filtered events for this service:', serviceEvents.length);
      setEvents(serviceEvents);
    } catch (err) {
      console.error('Error loading events:', err);
      setError(t.loadEventsError);
    } finally {
      setLoading(false);
    }
  };

  const loadEventDetails = async (id) => {
    try {
      const response = await axios.get(`${API}/events/${id}`);
      setSelectedEvent(response.data);
      setStatusUpdate({
        status: response.data.status,
        estimated_completion: response.data.estimated_completion ? 
          new Date(response.data.estimated_completion).toISOString().slice(0, 16) : '',
        priority: response.data.priority || 1
      });
    } catch (err) {
      console.error('Error loading event details:', err);
      setError(t.loadDetailsError);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedEvent) return;

    try {
      setUpdating(true);
      setError('');

      const updateData = {
        status: statusUpdate.status,
        priority: statusUpdate.priority
        // НЕ меняем assigned_to при обновлении статуса - оставляем как есть
      };

      if (statusUpdate.estimated_completion) {
        updateData.estimated_completion = new Date(statusUpdate.estimated_completion).toISOString();
      }

      await axios.put(`${API}/events/${selectedEvent.id}`, updateData);
      
      // Add comment if provided
      if (comment.comment_ru.trim() || comment.comment_kk.trim()) {
        await axios.post(`${API}/events/${selectedEvent.id}/comments`, {
          comment: comment.comment_ru.trim() || comment.comment_kk.trim()
        });
      }

      // Refresh data
      await loadEvents();
      await loadEventDetails(selectedEvent.id);
      
      // Reset comment form
      setComment({ comment_ru: '', comment_kk: '' });
      
      alert(t.statusUpdated);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error updating event:', err);
      setError(t.updateError);
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEvent = async () => {
    if (!selectedEvent) return;

    const reason = prompt(t.cancelReasonPrompt);
    if (!reason || !reason.trim()) {
      alert(t.cancelReasonRequired);
      return;
    }

    const confirmed = window.confirm(t.cancelConfirm(reason));
    if (!confirmed) return;

    setUpdating(true);
    setError('');

    try {
      // Update event status to 'rejected' and add comment
      await axios.put(`${API}/events/${selectedEvent.id}`, {
        status: 'rejected',
        priority: selectedEvent.priority // Keep existing priority
      });

      // Add mandatory comment about cancellation
      await axios.post(`${API}/events/${selectedEvent.id}/comments`, {
        comment: `СОБЫТИЕ ОТМЕНЕНО СЛУЖБОЙ. Причина: ${reason}`
      });

      // Refresh data
      await loadEvents();
      setSelectedEvent(null); // Clear selection since event is now cancelled
      
      alert(t.eventCancelled);
    } catch (err) {
      console.error('Error cancelling event:', err);
      setError(t.cancelError);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(dateLocale(language), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to get translated comment text 
  const getTranslatedCommentText = (comment) => {
    const originalText = comment.comment_ru || comment.comment || '';
    if (!originalText) return '';

    // Показываем комментарий на выбранном языке интерфейса, при необходимости переводим
    const detectedLang = detectLanguage(originalText);
    if (detectedLang !== language) {
      return translateText(originalText, detectedLang, language);
    }

    return originalText;
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': '#6b7c6e',
      'assigned': '#4a90a4',
      'in_progress': '#b8860b',
      'completed': '#2e7d4f',
      'rejected': '#8b5a5a'
    };
    return colors[status] || '#6b7c6e';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      1: '#8a9a8d',
      2: '#6b7c6e',
      3: '#b8860b',
      4: '#d2691e',
      5: '#8b5a5a'
    };
    return colors[priority] || '#6b7c6e';
  };

  return (
    <>
      <div className="service-panel-overlay">
        <div className="service-panel">
          <div className="service-panel-header">
            <h2>{t.panelTitle} - {serviceTypeName(user?.service_type, language, user?.full_name)}</h2>
            <div className="header-buttons">
              <button className="close-btn" onClick={onClose}>✕</button>
            </div>
          </div>

          <div className="service-panel-content">
          <div className="events-section">
            <h3>{t.activeEvents}</h3>
            
            {loading ? (
              <div className="loading">
                <div className="loading-spinner"></div>
                <p>{t.loadingEvents}</p>
              </div>
            ) : (
              <div className="events-list">
                {events.length === 0 ? (
                  <div className="no-events">{t.noActiveEvents}</div>
                ) : (
                  events.map(event => (
                    <div
                      key={event.id}
                      className={`event-card ${selectedEvent?.id === event.id ? 'selected' : ''}`}
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className="event-header">
                        <div className="event-category">
                          {t.categories[event.category] || `📍 ${event.category}`}
                        </div>
                        <div 
                          className="event-status-badge"
                          style={{ backgroundColor: getStatusColor(event.status) }}
                        >
                          {(() => {
                            const opt = statusOptions.find(s => s.value === event.status);
                            return opt ? (language === 'kk' ? opt.label_kk : opt.label_ru) : event.status;
                          })()}
                        </div>
                      </div>
                      
                      <div className="event-description">
                        {(language === 'kk' ? event.description_kk : event.description_ru) || event.description_ru || event.description_kk}
                      </div>
                      
                      <div className="event-meta">
                        <div 
                          className="event-priority"
                          style={{ color: getPriorityColor(event.priority) }}
                        >
                          {t.priority}: {event.priority}/5
                        </div>
                        <div className="event-time">
                          {formatDate(event.created_at)}
                        </div>
                      </div>
                      
                      {event.address && (
                        <div className="event-address">📍 {event.address}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {selectedEvent && (
            <div className="event-details-section">
              <h3>{t.eventDetails}</h3>
              
              <div className="event-details">
                <div className="detail-row">
                  <div className="detail-group">
                    <label>{t.description}</label>
                    <p>{(language === 'kk' ? selectedEvent.description_kk : selectedEvent.description_ru) || selectedEvent.description_ru || selectedEvent.description_kk}</p>
                  </div>
                  
                  <div className="detail-group">
                    <label>{t.priorityLabel}</label>
                    <span 
                      className="priority-badge"
                      style={{ color: getPriorityColor(selectedEvent.priority) }}
                    >
                      {selectedEvent.priority}/5
                    </span>
                  </div>
                </div>

                <div className="detail-row">
                  {selectedEvent.created_by_user && (
                    <div className="detail-group">
                      <label>{t.creator}</label>
                      <p>{selectedEvent.created_by_user.full_name}</p>
                    </div>
                  )}

                  <div className="detail-group">
                    <label>{t.address}</label>
                    <p>{selectedEvent.address || t.notSpecified}</p>
                  </div>
                </div>

                {(selectedEvent.reporter_full_name || selectedEvent.reporter_phone) && (
                  <div className="detail-row">
                    <div className="detail-group">
                      <label>{t.reporterName}</label>
                      <p data-testid="event-reporter-name">👤 {selectedEvent.reporter_full_name || t.notSpecified}</p>
                    </div>
                    <div className="detail-group">
                      <label>{t.reporterPhone}</label>
                      <p data-testid="event-reporter-phone">
                        {selectedEvent.reporter_phone ? (
                          <a href={`tel:${selectedEvent.reporter_phone}`} style={{ color: '#2563eb', fontWeight: 600 }}>
                            📞 {selectedEvent.reporter_phone}
                          </a>
                        ) : t.notSpecified}
                      </p>
                    </div>
                  </div>
                )}

                {selectedEvent.comments && selectedEvent.comments.length > 0 && (
                  <div className="detail-group full-width">
                    <label>{t.comments}</label>
                    <div className="comments-list">
                      {selectedEvent.comments.map(comment => (
                        <div key={comment.id} className="comment-item">
                          <div className="comment-header">
                            <span className="comment-author">{comment.user_name || t.userFallback}</span>
                            <span className="comment-date">{formatDate(comment.created_at)}</span>
                          </div>
                          <div className="comment-text">{getTranslatedCommentText(comment)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="status-update-section">
                <h4>{t.updateStatus}</h4>
                
                {error && (
                  <div className="error-message">{error}</div>
                )}

                <div className="update-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t.statusLabel}</label>
                      <select
                        className="service-compact-field status-field-compact"
                        value={statusUpdate.status}
                        onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {language === 'kk' ? option.label_kk : option.label_ru}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{t.priorityLabel}</label>
                      <select
                        className="service-compact-field priority-field-compact"
                        value={statusUpdate.priority}
                        onChange={(e) => setStatusUpdate(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                      >
                        {t.priorityOptions.map((label, idx) => (
                          <option key={idx + 1} value={idx + 1}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t.serviceComment}</label>
                    <textarea
                      className="auto-size-field"
                      value={comment.comment_ru}
                      onChange={(e) => {
                        setComment(prev => ({ ...prev, comment_ru: e.target.value }));
                        // Auto-resize textarea
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      placeholder={t.commentPlaceholder}
                      rows="2"
                      style={{ minHeight: '60px', maxHeight: '120px' }}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      className="update-btn"
                      onClick={handleStatusUpdate}
                      disabled={updating}
                    >
                      {updating ? (
                        <>
                          <span className="button-spinner"></span>
                          {t.updating}
                        </>
                      ) : (
                        t.updateBtn
                      )}
                    </button>
                    
                    <button
                      className="cancel-event-btn"
                      onClick={handleCancelEvent}
                      disabled={updating}
                    >
                      {t.cancelBtn}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div> {/* Close service-panel-content */}
        </div>
      </div>
    </>
  );
};

export default ServicePanel;